import React from 'react'
import { Link } from 'react-router-dom'
import { LABELS } from '../constants/staticText'
import JobContent from './JobContent'
import JobSocial from './JobSocial'
import JobMedia from './JobMedia'
import { useFileUrl } from '../functions/FileUrlContext'
import ExhibitorProfileLink from '../../lib/components/ExhibitorProfileLink'
import RichText from '../../lib/components/RichText'
import VisuallyHidden from '../../lib/a11y/VisuallyHidden'
import { bildMasse } from '../../lib/functions/bildMasse'

// Aufbau 1:1 wie auf der Live-Seite: Rücklink, Kopfband mit dem Firmennamen,
// Titel, drei Eckdaten mit Symbolen, Firmenname und -beschreibung, Logo rechts,
// drei Knöpfe, Textblöcke, Kontaktangaben, Bewerbungsbalken und am Ende die
// weiteren Anzeigen derselben Firma (#22).
const datumDeutsch = (wert) => {
    if (!wert) return ''
    const [jahr, monat, tag] = String(wert).slice(0, 10).split('-')
    return `${tag}.${monat}.${jahr}`
}

const Rueckweg = () => (
    <Link className="job-detail-back" to="/stellenangebote/">
        <span className="job-detail-back-icon">«</span> {LABELS.back}
    </Link>
)

const Eckdaten = ({ job }) => (
    <ul className="job-detail-facts">
        <li className="job-detail-fact is-type">{job.jobType}</li>
        {job.location && <li className="job-detail-fact is-town">{job.location}</li>}
        {job.publishedAt && (
            <li className="job-detail-fact is-published">
                {LABELS.publishedOn} {datumDeutsch(job.publishedAt)}
            </li>
        )}
    </ul>
)

const Knoepfe = ({ job, fileUrl }) => (
    <div className="job-detail-buttons">
        {job.media?.pdf && (
            <a className="job-detail-button is-download" href={fileUrl(job.media.pdf.fileId)} target="_blank" rel="noreferrer">
                {LABELS.download}
                <VisuallyHidden>{LABELS.opensNewTab}</VisuallyHidden>
            </a>
        )}
        <button type="button" className="job-detail-button is-print" onClick={() => window.print()}>
            {LABELS.print}
        </button>
        {job.company.website && (
            <a className="job-detail-button is-website" href={job.company.website} target="_blank" rel="noreferrer">
                {LABELS.companyWebsite}
                <VisuallyHidden>{LABELS.opensNewTab}</VisuallyHidden>
            </a>
        )}
    </div>
)

// Kontaktdaten wie auf der Live-Anzeige: E-Mail, Telefon, darunter die
// Anschrift mit Firmenname, Strasse und PLZ Ort (#203).
const hatAnschrift = address => !!(address && (address.street || address.zip || address.city))

const Kontakt = ({ job }) => {
    const { email, phone } = job.contact || {}
    const { address } = job
    if (!email && !phone && !hatAnschrift(address)) return null

    return (
        <div className="job-detail-contact">
            <h2>{LABELS.contactHeading}</h2>
            {email && <p>{LABELS.email} <a className="job-detail-mail" href={`mailto:${email}`}>{email}</a></p>}
            {phone && <p>{LABELS.phone} <a href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a></p>}
            {hatAnschrift(address) && (
                <p className="job-detail-address">
                    <strong>{job.company.name}</strong><br />
                    {address.street && <>{address.street}<br /></>}
                    {[address.zip, address.city].filter(Boolean).join(' ')}
                </p>
            )}
        </div>
    )
}

// Die Anzeige selbst, ohne Laden und ohne den Rahmen der Seite (Brotkrumen):
// die oeffentliche Detailseite und die Vorschau im Backoffice zeigen dasselbe
// Markup. children steht vor dem letzten Rueckweg — dort haengt die Seite die
// weiteren Anzeigen der Firma an.
const JobDetailView = ({ job, headingRef = null, children = null }) => {
    const fileUrl = useFileUrl()

    return (
        <article className="job-detail">
            <Rueckweg />

            <div className="job-detail-banner">{LABELS.offerFrom} {job.company.name}</div>

            <div className="job-detail-head">
                <div className="job-detail-head-text">
                    <h1 tabIndex={-1} ref={headingRef}>{job.title}</h1>
                    <Eckdaten job={job} />
                    <p className="job-detail-company">
                        {/* Der Firmenname fuehrt auf die Aussteller-Profilseite (#239),
                            wenn es sie oeffentlich gibt (company.profile vom Server). */}
                        {job.company.profile
                            ? <ExhibitorProfileLink id={job.company.id}>{job.company.name}</ExhibitorProfileLink>
                            : job.company.name}
                    </p>
                    {job.company.description && <RichText className="job-detail-company-text" html={job.company.description} />}
                </div>
                {job.company.logo && (
                    <div className="job-detail-logo">
                        <img src={fileUrl(job.company.logo.fileId)} alt={job.company.name} {...bildMasse(job.company.logo)} />
                    </div>
                )}
            </div>

            <Knoepfe job={job} fileUrl={fileUrl} />

            {/* Bildleiste und Text in einem Block: ein einzelnes Bild schwimmt
                links neben dem Text (#210) und darf nicht bis in die
                Kontaktdaten reichen — der Block schliesst den Umfluss ab. */}
            <div className="job-detail-body">
                <JobMedia media={job.media} />
                <JobContent markdown={job.content} />
            </div>

            <Kontakt job={job} />

            {job.contact?.applyUrl && (
                <a className="job-detail-apply" href={job.contact.applyUrl} target="_blank" rel="noreferrer">
                    {LABELS.applyLead} <strong>{LABELS.applyLink}</strong>
                </a>
            )}

            <JobSocial social={job.social} />

            {children}

            <Rueckweg />
        </article>
    )
}

export default JobDetailView
